import { trpc } from "@/lib/trpc";
import { checkTradingHours, smartInterval } from "@/lib/timeUtils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Wifi, WifiOff, Activity, Shield, Server, Gauge, Clock,
  RefreshCw, Loader2, AlertTriangle, ChevronRight, Zap,
  ShieldAlert, CheckCircle2, XCircle, Radio, Info, Monitor,
  ArrowUpDown, Download, Eye, ChevronDown, ChevronUp, BarChart3,
  TrendingUp, TrendingDown, HeartPulse, Pause, Play, Send, Cpu,
} from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { RestartHistoryWidget } from "@/components/RestartHistoryWidget";

// ========== 北京时间格式化工具 ==========
const BJ_TZ = "Asia/Shanghai";
function toBJTime(d: Date | string | number) {
  return new Date(typeof d === 'string' || typeof d === 'number' ? new Date(d) : d);
}
function formatBJTime(d: Date | string | number, fmt: 'time' | 'datetime' | 'short' = 'time') {
  const date = toBJTime(d);
  if (fmt === 'time') return date.toLocaleTimeString('zh-CN', { timeZone: BJ_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  if (fmt === 'short') return date.toLocaleTimeString('zh-CN', { timeZone: BJ_TZ, hour: '2-digit', minute: '2-digit' });
  return date.toLocaleString('zh-CN', { timeZone: BJ_TZ });
}
function getBJHours(d: Date | string | number) {
  const date = toBJTime(d);
  return parseInt(date.toLocaleString('en-US', { timeZone: BJ_TZ, hour: 'numeric', hour12: false }));
}
function getBJMinutes(d: Date | string | number) {
  const date = toBJTime(d);
  return parseInt(date.toLocaleString('en-US', { timeZone: BJ_TZ, minute: 'numeric' }));
}
function formatBJDate(d: Date | string | number) {
  const date = toBJTime(d);
  return date.toLocaleDateString('zh-CN', { timeZone: BJ_TZ, month: 'numeric', day: 'numeric' });
}

// ========== 延迟等级颜色映射 ==========
const LATENCY_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  excellent: { bg: "bg-emerald-500/20", text: "text-emerald-400", label: "极佳" },
  good: { bg: "bg-green-500/20", text: "text-green-400", label: "良好" },
  fair: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "一般" },
  poor: { bg: "bg-red-500/20", text: "text-red-400", label: "较差" },
  unknown: { bg: "bg-muted", text: "text-muted-foreground", label: "未知" },
};

// ========== 连接状态颜色映射 ==========
const CONNECTION_COLORS: Record<string, { bg: string; text: string; icon: any; pulse?: boolean }> = {
  connected: { bg: "bg-emerald-500/15 border-emerald-500/30", text: "text-emerald-400", icon: Wifi, pulse: true },
  error: { bg: "bg-red-500/15 border-red-500/30", text: "text-red-400", icon: AlertTriangle },
  timeout: { bg: "bg-orange-500/15 border-orange-500/30", text: "text-orange-400", icon: Clock },
  disconnected: { bg: "bg-muted/50 border-border/50", text: "text-muted-foreground", icon: WifiOff },
};

const TIME_RANGES = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
] as const;

// ========== 延迟指示器（圆点 + 数字） ==========
function LatencyIndicator({ ms, level }: { ms: number; level: string }) {
  const color = LATENCY_COLORS[level] || LATENCY_COLORS.unknown;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5">
            <span className={`inline-block h-2 w-2 rounded-full ${level === 'excellent' ? 'bg-emerald-400' : level === 'good' ? 'bg-green-400' : level === 'fair' ? 'bg-yellow-400' : level === 'poor' ? 'bg-red-400' : 'bg-muted-foreground'} ${level === 'excellent' || level === 'good' ? 'animate-pulse' : ''}`} />
            <span className={`text-xs font-mono ${color.text}`}>
              {ms > 0 ? `${ms}ms` : "--"}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">Manus → 交易引擎往返延迟: {ms > 0 ? `${ms}ms (${color.label})` : "未连接"}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {level === 'excellent' ? '< 100ms 极佳' : level === 'good' ? '100-300ms 良好' : level === 'fair' ? '300-800ms 一般' : level === 'poor' ? '> 800ms 较差' : '未检测'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ========== SVG 延迟趋势折线图 ==========
function LatencyChart({ hours }: { hours: number }) {
  const _isTH = checkTradingHours().isTradingHours;
  const { data, isLoading } = trpc.tradingProxy.latencyHistory.useQuery(
    { hours },
    { refetchInterval: smartInterval(60000, _isTH, 300000), staleTime: 30000 }
  );
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; ms: number; time: string; status: string } | null>(null);

  const chartData = useMemo(() => {
    if (!data?.history || data.history.length === 0) return null;
    // 过滤掉disconnected的数据点用于绘图，但保留断连标记
    const points = data.history.map((h: any) => ({
      ms: h.latencyMs,
      time: new Date(h.createdAt),
      status: h.status,
      level: h.level,
    }));
    return points;
  }, [data]);

  if (isLoading) {
    return (
      <div className="h-32 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!chartData || chartData.length < 2) {
    return (
      <div className="h-32 flex items-center justify-center text-xs text-muted-foreground">
        暂无足够的延迟数据（至少需要2个数据点）
      </div>
    );
  }

  // 计算图表参数
  const W = 600;
  const H = 120;
  const PAD = { top: 10, right: 40, bottom: 24, left: 45 };
  const cw = W - PAD.left - PAD.right;
  const ch = H - PAD.top - PAD.bottom;

  const connectedPoints = chartData.filter((p: any) => p.status === 'connected' && p.ms > 0);
  const maxMs = connectedPoints.length > 0 ? Math.max(...connectedPoints.map((p: any) => p.ms), 100) : 100;
  const minMs = 0;
  const timeMin = chartData[0].time.getTime();
  const timeMax = chartData[chartData.length - 1].time.getTime();
  const timeRange = Math.max(timeMax - timeMin, 1);

  const scaleX = (t: number) => PAD.left + ((t - timeMin) / timeRange) * cw;
  const scaleY = (ms: number) => PAD.top + ch - (Math.min(ms, maxMs) / maxMs) * ch;

  // 生成折线路径（只连接connected的点）
  let pathD = "";
  let areaD = "";
  let prevConnected = false;
  connectedPoints.forEach((p: any, i: number) => {
    const x = scaleX(p.time.getTime());
    const y = scaleY(p.ms);
    if (i === 0 || !prevConnected) {
      pathD += `M${x},${y}`;
      areaD += `M${x},${PAD.top + ch}L${x},${y}`;
    } else {
      pathD += `L${x},${y}`;
      areaD += `L${x},${y}`;
    }
    prevConnected = true;
  });
  // 关闭area路径
  if (connectedPoints.length > 0) {
    const lastX = scaleX(connectedPoints[connectedPoints.length - 1].time.getTime());
    areaD += `L${lastX},${PAD.top + ch}Z`;
  }

  // 断连区域标记
  const disconnectRanges: Array<{ x1: number; x2: number }> = [];
  let dcStart: number | null = null;
  chartData.forEach((p: any, i: number) => {
    if (p.status !== 'connected') {
      if (dcStart === null) dcStart = scaleX(p.time.getTime());
    } else {
      if (dcStart !== null) {
        disconnectRanges.push({ x1: dcStart, x2: scaleX(p.time.getTime()) });
        dcStart = null;
      }
    }
  });
  if (dcStart !== null) {
    disconnectRanges.push({ x1: dcStart, x2: PAD.left + cw });
  }

  // Y轴刻度
  const yTicks = [0, Math.round(maxMs / 4), Math.round(maxMs / 2), Math.round(maxMs * 3 / 4), maxMs];

  // X轴时间标签
  const xLabels: Array<{ x: number; label: string }> = [];
  const labelCount = Math.min(6, chartData.length);
  for (let i = 0; i < labelCount; i++) {
    const idx = Math.round((i / (labelCount - 1)) * (chartData.length - 1));
    const p = chartData[idx];
    const x = scaleX(p.time.getTime());
    const t = p.time;
    const bjH = getBJHours(t).toString().padStart(2, '0');
    const bjM = getBJMinutes(t).toString().padStart(2, '0');
    const label = hours <= 24
      ? `${bjH}:${bjM}`
      : `${formatBJDate(t)} ${bjH}:00`;
    xLabels.push({ x, label });
  }

  // 阈值线
  const thresholds = [
    { ms: 100, color: "rgba(52,211,153,0.3)", label: "100ms" },
    { ms: 300, color: "rgba(250,204,21,0.3)", label: "300ms" },
    { ms: 800, color: "rgba(248,113,113,0.3)", label: "800ms" },
  ].filter(t => t.ms < maxMs);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;
    // 找最近的数据点
    let closest: any = null;
    let minDist = Infinity;
    chartData.forEach((p: any) => {
      const px = scaleX(p.time.getTime());
      const dist = Math.abs(px - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = p;
      }
    });
    if (closest && minDist < 20) {
      const px = scaleX(closest.time.getTime());
      const py = closest.status === 'connected' ? scaleY(closest.ms) : PAD.top + ch / 2;
      setTooltip({
        x: px,
        y: py,
        ms: closest.ms,
        time: formatBJTime(closest.time, 'time'),
        status: closest.status,
      });
    } else {
      setTooltip(null);
    }
  };

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-32"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* 网格线 */}
        {yTicks.map((tick) => (
          <line key={tick} x1={PAD.left} y1={scaleY(tick)} x2={PAD.left + cw} y2={scaleY(tick)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
        ))}

        {/* 阈值线 */}
        {thresholds.map((t) => (
          <g key={t.ms}>
            <line x1={PAD.left} y1={scaleY(t.ms)} x2={PAD.left + cw} y2={scaleY(t.ms)}
              stroke={t.color} strokeWidth="0.5" strokeDasharray="4,4" />
          </g>
        ))}

        {/* 断连区域 */}
        {disconnectRanges.map((r, i) => (
          <rect key={i} x={r.x1} y={PAD.top} width={Math.max(r.x2 - r.x1, 2)} height={ch}
            fill="rgba(248,113,113,0.08)" />
        ))}

        {/* 面积填充 */}
        {areaD && <path d={areaD} fill="url(#latencyGradient)" opacity="0.3" />}

        {/* 折线 */}
        {pathD && <path d={pathD} fill="none" stroke="rgba(96,165,250,0.8)" strokeWidth="1.5" strokeLinejoin="round" />}

        {/* 断连标记点 */}
        {chartData.filter((p: any) => p.status !== 'connected').map((p: any, i: number) => (
          <g key={`dc-${i}`}>
            <circle cx={scaleX(p.time.getTime())} cy={PAD.top + ch / 2} r="3"
              fill="rgba(248,113,113,0.6)" stroke="rgba(248,113,113,0.8)" strokeWidth="0.5" />
            <line x1={scaleX(p.time.getTime())} y1={PAD.top + ch / 2 - 4}
              x2={scaleX(p.time.getTime())} y2={PAD.top + ch / 2 + 4}
              stroke="rgba(248,113,113,0.6)" strokeWidth="1" />
          </g>
        ))}

        {/* Y轴标签 */}
        {yTicks.map((tick) => (
          <text key={tick} x={PAD.left - 4} y={scaleY(tick) + 3}
            textAnchor="end" fill="rgba(255,255,255,0.4)" fontSize="8" fontFamily="monospace">
            {tick}
          </text>
        ))}

        {/* X轴标签 */}
        {xLabels.map((l, i) => (
          <text key={i} x={l.x} y={H - 4}
            textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontFamily="monospace">
            {l.label}
          </text>
        ))}

        {/* Tooltip 十字线 */}
        {tooltip && (
          <>
            <line x1={tooltip.x} y1={PAD.top} x2={tooltip.x} y2={PAD.top + ch}
              stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" strokeDasharray="2,2" />
            <circle cx={tooltip.x} cy={tooltip.y} r="3"
              fill={tooltip.status === 'connected' ? 'rgba(96,165,250,1)' : 'rgba(248,113,113,1)'}
              stroke="white" strokeWidth="1" />
          </>
        )}

        {/* 渐变定义 */}
        <defs>
          <linearGradient id="latencyGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(96,165,250,0.4)" />
            <stop offset="100%" stopColor="rgba(96,165,250,0)" />
          </linearGradient>
        </defs>
      </svg>

      {/* Tooltip 浮层 */}
      {tooltip && (
        <div
          className="absolute pointer-events-none bg-popover/95 border border-border rounded-md px-2 py-1 shadow-lg"
          style={{
            left: `${(tooltip.x / W) * 100}%`,
            top: `${(tooltip.y / H) * 100 - 15}%`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="text-[10px] text-muted-foreground">{tooltip.time}</div>
          <div className={`text-xs font-mono font-bold ${tooltip.status === 'connected' ? 'text-blue-400' : 'text-red-400'}`}>
            {tooltip.status === 'connected' ? `${tooltip.ms}ms` : '断连'}
          </div>
        </div>
      )}

      {/* 统计摘要 */}
      {data?.stats && data.stats.count > 0 && (
        <div className="flex items-center gap-4 mt-1.5 px-1">
          <span className="text-[10px] text-muted-foreground">
            平均 <span className="font-mono text-foreground">{data.stats.avg}ms</span>
          </span>
          <span className="text-[10px] text-muted-foreground">
            最低 <span className="font-mono text-emerald-400">{data.stats.min}ms</span>
          </span>
          <span className="text-[10px] text-muted-foreground">
            最高 <span className="font-mono text-red-400">{data.stats.max}ms</span>
          </span>
          {data.stats.disconnectCount > 0 && (
            <span className="text-[10px] text-red-400">
              断连 {data.stats.disconnectCount}次
            </span>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto">
            共 {data.stats.count} 个数据点
          </span>
        </div>
      )}
    </div>
  );
}

// ========== 详情弹窗 ==========
function EngineDetailDialog({ open, onOpenChange, data, heartbeatData, webVersionData }: { open: boolean; onOpenChange: (v: boolean) => void; data: any; heartbeatData: any; webVersionData: any }) {
  if (!data) return null;
  
  const sections = [
    {
      title: "连接信息",
      icon: Wifi,
      items: [
        { label: "连接状态", value: data.connection?.status === 'connected' ? '已连接' : data.connection?.status === 'timeout' ? '超时' : data.connection?.status === 'error' ? '错误' : '未连接' },
        { label: "延迟", value: data.latency?.ms > 0 ? `${data.latency.ms}ms (${LATENCY_COLORS[data.latency.level]?.label || '未知'})` : '--' },
        { label: "代理地址", value: data.configInfo?.tradingProxyUrl || '--' },
        { label: "消息", value: data.connection?.message || '--' },
      ],
    },
    {
      title: "引擎状态",
      icon: Server,
      items: [
        { label: "运行状态", value: data.engine?.status || '--' },
        { label: "运行模式", value: data.engine?.mode === 'live' ? '实盘' : data.engine?.mode === 'sim' ? '模拟' : (data.configInfo?.tradingProxyMode === 'live' ? '实盘' : '模拟') },
        { label: "行情等级", value: (data.engine?.marketDataLevel || (data.engine?.dataSource === "nats_l2" ? "L2" : "L1")) === "L2" ? "L2（NATS逐笔）" : "L1（五档/腾讯）" },
        { label: "XT连接", value: data.engine?.xtConnected === true ? '已连接' : data.engine?.xtConnected === false ? '未连接' : '--' },
        { label: "活跃策略", value: data.engine?.activeStrategies != null ? `${data.engine.activeStrategies} 个` : ((data as any).webAgentCount != null ? `${(data as any).webAgentCount} 个 (Web)` : '--') },
        { label: "版本", value: data.engine?.version || '--' },
        { label: "运行时长", value: data.engine?.uptime ? formatUptime(data.engine.uptime) : '--' },
        { label: "启动时间", value: data.engine?.startedAt ? formatBJTime(data.engine.startedAt, 'datetime') : '--' },
        { label: "资金账号", value: data.engine?.accountId || '--' },
      ],
    },
    {
      title: "风控状态",
      icon: Shield,
      items: data.riskControl ? [
        { label: "日亏损", value: `¥${data.riskControl.dailyLoss?.toFixed(2) || '0.00'} / ¥${data.riskControl.maxDailyLoss?.toFixed(2) || '--'}` },
        { label: "日交易次数", value: `${data.riskControl.dailyTradeCount || 0} / ${data.riskControl.maxDailyTrades || '--'}` },
        { label: "连续止损", value: `${data.riskControl.consecutiveStopLoss || 0} 次` },
        { label: "是否冻结", value: data.riskControl.isFrozen ? `是 (${data.riskControl.frozenReason || '未知原因'})` : '否' },
      ] : [{ label: "状态", value: "无数据（交易引擎未返回风控信息）" }],
    },
    {
      title: "心跳监控",
      icon: HeartPulse,
      items: [
        { label: "心跳状态", value: heartbeatData?.pausedByHeartbeat ? '已暂停（策略自动暂停）' : heartbeatData?.connected ? '正常' : '离线' },
        { label: "最近心跳", value: heartbeatData?.lastHeartbeat ? formatBJTime(heartbeatData.lastHeartbeat, 'datetime') : '--' },
        { label: "距上次心跳", value: heartbeatData?.elapsedMs ? `${Math.round(heartbeatData.elapsedMs / 1000)}秒` : '--' },
        { label: "连续失败次数", value: String(heartbeatData?.consecutiveFails ?? '--') },
        { label: "心跳暂停", value: heartbeatData?.pausedByHeartbeat ? '是' : '否' },
        // 引擎线程健康状态
        ...(() => {
          const th = heartbeatData?.threadHealth;
          if (!th || Object.keys(th).length === 0) return [{ label: "引擎线程", value: "无运行中的线程" }];
          return Object.entries(th).map(([mid, info]: [string, any]) => ({
            label: `线程 #${mid}`,
            value: `${info.alive ? '✅ 存活' : '❌ 已死亡'} | 上次tick: ${info.staleSec >= 0 ? `${info.staleSec}s前` : '--'} | 错误: ${info.errorCount} | 重启: ${info.restartCount}`,
          }));
        })(),
      ],
    },
    {
      title: "版本与更新",
      icon: Download,
      items: [
        { label: "引擎端版本", value: data.engine?.version || data.autoUpdate?.currentVersion || '--' },
        { label: "Web端代码版本", value: webVersionData?.fileVersion || '--' },
        { label: "Web端已发布版本", value: webVersionData?.latestPublishedVersion || '--' },
        { label: "版本同步状态", value: (() => {
          const engineV = data.engine?.version || data.autoUpdate?.currentVersion;
          const webV = webVersionData?.latestPublishedVersion;
          if (!engineV || !webV) return '未知';
          return engineV === webV ? '✅ 已同步' : `⚠️ 不一致 (引擎 ${engineV} / Web ${webV})`;
        })() },
        { label: "待更新", value: data.autoUpdate?.pendingUpdate ? `是 → ${data.autoUpdate.pendingVersion}` : '否' },
        { label: "上次检查", value: data.autoUpdate?.lastCheckTime ? formatBJTime(data.autoUpdate.lastCheckTime, 'datetime') : '--' },
        { label: "历史版本数", value: String(webVersionData?.totalVersions ?? '--') },
      ],
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-primary" />
            交易引擎详细状态
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.title}>
              <h4 className="text-sm font-medium flex items-center gap-1.5 mb-2">
                <section.icon className="h-3.5 w-3.5 text-primary" />
                {section.title}
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pl-5">
                {section.items.map((item) => (
                  <div key={item.label} className="contents">
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                    <span className="text-xs font-mono text-foreground break-all">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}秒`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}小时${m}分`;
}

// ========== 触发更新按钮 ==========
function TriggerUpdateButton({ targetVersion }: { targetVersion: string }) {
  const triggerMut = trpc.engineUpdate.triggerUpdate.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`更新命令已下发: ${data.targetVersion}`);
      } else {
        toast.error(`下发失败: ${data.error}`);
      }
    },
    onError: (e) => toast.error(`下发失败: ${e.message}`),
  });

  return (
    <button
      className="mt-1 flex items-center gap-0.5 text-[10px] text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        triggerMut.mutate({ targetVersion });
      }}
      disabled={triggerMut.isPending}
    >
      {triggerMut.isPending ? (
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
      ) : (
        <Send className="h-2.5 w-2.5" />
      )}
      推送更新
    </button>
  );
}

// ========== 实时时钟 Hook ==========
function useRealtimeClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

// ========== 主面板组件 ==========
export default function EngineStatusPanel({ dbRunningCount = 0 }: { dbRunningCount?: number }) {
  const bjNow = useRealtimeClock();
  const _isTH = checkTradingHours().isTradingHours;
  // 延迟2秒启用，避免首屏阻塞（VPN连接引擎可能超时）
  const [panelEnabled, setPanelEnabled] = useState(false);
  useEffect(() => { const t = setTimeout(() => setPanelEnabled(true), 2000); return () => clearTimeout(t); }, []);

  const { data, isLoading, refetch, isFetching } = trpc.tradingProxy.dashboard.useQuery(undefined, {
    enabled: panelEnabled,
    refetchInterval: panelEnabled ? smartInterval(15000, _isTH, 120000) : false,
    staleTime: 10000,
    retry: 0, // 超时不重试，避免长时间卡住
  });
  // [v2.0 2026-04-29] HA 系统状态 (主备角色 + 节点健康)
  const { data: systemState } = trpc.system.getState.useQuery(undefined, {
    refetchInterval: smartInterval(15000, 60000),
    refetchIntervalInBackground: true,
  });

  // 心跳状态查询
  const { data: heartbeatData } = trpc.tradingProxy.heartbeatStatus.useQuery(undefined, {
    enabled: panelEnabled,
    refetchInterval: panelEnabled ? smartInterval(15000, _isTH, 120000) : false,
    staleTime: 10000,
    retry: 0,
  });
  // Web端最新版本查询
  const { data: webVersionData } = trpc.engineUpdate.currentWebVersion.useQuery(undefined, {
    enabled: panelEnabled,
    refetchInterval: panelEnabled ? smartInterval(60000, _isTH, 300000) : false,
    staleTime: 30000,
    retry: 0,
  });
  // 更新进度查询（仅交易时段高频）
  const { data: progressData } = trpc.engineUpdate.updateProgress.useQuery(undefined, {
    enabled: panelEnabled,
    refetchInterval: panelEnabled ? smartInterval(5000, _isTH, 60000) : false,
    staleTime: 4000,
    retry: 0,
  });
  const utils = trpc.useUtils();
  const clearProgressMut = trpc.engineUpdate.clearProgress.useMutation({
    onSuccess: () => {
      utils.engineUpdate.updateProgress.invalidate();
    },
  });
  const [showDetail, setShowDetail] = useState(false);
  const [showChart, setShowChart] = useState(false);
  // [Steven 2026-05-28] 引擎状态卡折叠: 全正常时只显示标题行 (备机异常不计)
  const [statusExpanded, setStatusExpanded] = useState(false);
  const [chartHours, setChartHours] = useState(24);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  useEffect(() => {
    if (data && !isFetching) {
      setLastRefresh(new Date());
    }
  }, [data, isFetching]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // 未启用时显示轻量占位（不阻塞首屏）
  if (!panelEnabled || isLoading) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>引擎状态加载中...</span>
        </CardContent>
      </Card>
    );
  }

  // 未配置状态
  if (!data?.configured) {
    return (
      <Card className="bg-card/50 border-border/50 border-dashed">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">交易引擎</span>
              <Badge variant="outline" className="text-[10px] border-muted-foreground/30">未配置</Badge>
            </div>
            <span className="text-[10px] text-muted-foreground">
              前往「券商配置」页面配置兴业SMT-Q交易代理
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const connStatus = data.connection?.status || 'disconnected';
  const connStyle = CONNECTION_COLORS[connStatus] || CONNECTION_COLORS.disconnected;
  const ConnIcon = connStyle.icon;
  const latencyLevel = data.latency?.level || 'unknown';
  const isConnected = connStatus === 'connected';
  const engineMode = data.engine?.mode === 'live' ? '实盘' : '模拟';
  const marketLevel = (
    data.engine?.marketDataLevel ||
    (data.engine?.dataSource === 'nats_l2' ? 'L2' : 'L1')
  );
  const isLive = data.engine?.mode === 'live' || data.configInfo?.tradingProxyMode === 'live';

  // [Steven 2026-05-28] 主引擎是否全健康 (忽略备机). 用于自动折叠卡片
  const _thMain = heartbeatData?.threadHealth;
  const _thEntries = _thMain ? Object.entries(_thMain) : [];
  const _deadCount = _thEntries.filter(([, info]: [string, any]) => !info.alive).length;
  const allHealthy = (
    isConnected &&
    !!heartbeatData?.connected &&
    !heartbeatData?.pausedByHeartbeat &&
    !data.riskControl?.isFrozen &&
    _deadCount === 0 &&
    (progressData == null || progressData.phase === 'idle')
  );
  // 折叠区是否展示: 手动展开 OR 主引擎有异常 (备机不计)
  const showBody = statusExpanded || !allHealthy;

  return (
    <>
      <Card className={`border ${connStyle.bg} transition-colors duration-300`}>
        <CardContent className="p-4">
          {/* 系统时间显示 */}
          <div className="flex items-center justify-between mb-2 px-0.5">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">系统时间</span>
            </div>
            <span className="text-xs font-mono text-foreground/80 tabular-nums">
              {bjNow.toLocaleString('zh-CN', {
                timeZone: BJ_TZ,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                weekday: 'short',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
              })}
            </span>
          </div>

          {/* 标题行 */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <ConnIcon className={`h-4 w-4 ${connStyle.text}`} />
                {connStyle.pulse && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 animate-ping" />}
              </div>
              <span className="text-sm font-medium">交易引擎</span>
              <Badge
                variant="outline"
                className={`text-[10px] ${isConnected ? 'border-emerald-500/40 text-emerald-400' : connStatus === 'timeout' ? 'border-orange-500/40 text-orange-400' : connStatus === 'error' ? 'border-red-500/40 text-red-400' : 'border-muted-foreground/30 text-muted-foreground'}`}
              >
                {isConnected ? '在线' : connStatus === 'timeout' ? '超时' : connStatus === 'error' ? '异常' : '离线'}
              </Badge>
              <LatencyIndicator ms={data.latency?.ms || -1} level={latencyLevel} />
            </div>
            <div className="flex items-center gap-1">
              {lastRefresh && (
                <span className="text-[10px] text-muted-foreground mr-1 hidden sm:inline">
                  {formatBJTime(lastRefresh, 'short')}
                </span>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowChart(!showChart)}>
                      <BarChart3 className={`h-3 w-3 ${showChart ? 'text-primary' : ''}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">{showChart ? '收起' : '展开'}延迟趋势图</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleRefresh} disabled={isFetching}>
                      {isFetching ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">刷新状态</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowDetail(true)}>
                      <Info className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">查看详细状态</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* [Steven 2026-05-28] 折叠/展开按钮: 全正常时默认折叠 */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setStatusExpanded(v => !v)}>
                      {showBody ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">{showBody ? '收起' : '展开'}引擎详情</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* 更新进度条 */}
          {progressData && progressData.phase !== 'idle' && (
            <div className={`rounded-lg p-3 mb-2 border ${
              progressData.phase === 'completed' ? 'bg-emerald-500/10 border-emerald-500/30' :
              progressData.phase === 'failed' ? 'bg-red-500/10 border-red-500/30' :
              progressData.phase === 'rolling_back' ? 'bg-orange-500/10 border-orange-500/30' :
              'bg-blue-500/10 border-blue-500/30'
            }`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  {progressData.phase === 'completed' ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  ) : progressData.phase === 'failed' ? (
                    <XCircle className="h-3.5 w-3.5 text-red-400" />
                  ) : progressData.phase === 'rolling_back' ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-orange-400 animate-pulse" />
                  ) : (
                    <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin" />
                  )}
                  <span className="text-xs font-medium">
                    {progressData.phase === 'completed' ? '更新完成' :
                     progressData.phase === 'failed' ? '更新失败' :
                     progressData.phase === 'rolling_back' ? '正在回滚...' :
                     progressData.phase === 'checking' ? '检查中...' :
                     progressData.phase === 'downloading' ? '下载中...' :
                     progressData.phase === 'verifying' ? '验证中...' :
                     progressData.phase === 'stopping' ? '停止服务...' :
                     progressData.phase === 'extracting' ? '解压中...' :
                     progressData.phase === 'migrating' ? '数据库迁移...' :
                     progressData.phase === 'restarting' ? '重启中...' : '更新中...'}
                  </span>
                  {progressData.version && (
                    <Badge variant="outline" className="text-[9px] h-4 px-1">
                      v{progressData.version}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground">{progressData.percent}%</span>
                  {['completed', 'failed'].includes(progressData.phase) && (
                    <button
                      className="text-xs px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-foreground cursor-pointer border border-border"
                      onClick={() => clearProgressMut.mutate()}
                      disabled={clearProgressMut.isPending}
                    >{clearProgressMut.isPending ? '清除中...' : '✕ 清除'}</button>
                  )}
                </div>
              </div>
              {/* 进度条 */}
              <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    progressData.phase === 'completed' ? 'bg-emerald-500' :
                    progressData.phase === 'failed' ? 'bg-red-500' :
                    progressData.phase === 'rolling_back' ? 'bg-orange-500' :
                    'bg-blue-500'
                  }`}
                  style={{ width: `${progressData.percent}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{progressData.message}</p>
              {progressData.error && (
                <p className="text-[10px] text-red-400 mt-0.5">错误: {progressData.error}</p>
              )}
            </div>
          )}

          {/* [Steven 2026-05-28] 折叠区: 全正常折叠, 只留标题行 (备机异常不计) */}
          {showBody && (<>
          {/* 状态卡片网格 */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {/* 连接状态 */}
            <div className={`rounded-lg p-2.5 ${isConnected ? 'bg-emerald-500/10' : 'bg-muted/30'}`}>
              <div className="flex items-center gap-1 mb-1">
                <Wifi className={`h-3 w-3 ${isConnected ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                <span className="text-[10px] text-muted-foreground">连接</span>
              </div>
              <div className={`text-sm font-bold ${isConnected ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                {isConnected ? '已连接' : connStatus === 'timeout' ? '超时' : connStatus === 'error' ? '异常' : '离线'}
              </div>
              <div className="text-[10px] text-muted-foreground truncate" title={data.connection?.message}>
                {data.connection?.message || '--'}
              </div>
            </div>

            {/* 运行模式 */}
            <div className={`rounded-lg p-2.5 ${isLive ? 'bg-red-500/10' : 'bg-blue-500/10'}`}>
              <div className="flex items-center gap-1 mb-1">
                <Zap className={`h-3 w-3 ${isLive ? 'text-red-400' : 'text-blue-400'}`} />
                <span className="text-[10px] text-muted-foreground">模式</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-sm font-bold ${isLive ? 'text-red-400' : 'text-blue-400'}`}>
                  {engineMode}
                </span>
                <Badge variant="outline" className={`text-[9px] px-1 py-0 h-4 ${marketLevel === 'L2' ? 'border-amber-500/40 text-amber-400' : 'border-muted-foreground/30 text-muted-foreground'}`}>
                  {marketLevel}
                </Badge>
              </div>
              <div className="text-[10px] text-muted-foreground">
                {data.engine?.activeStrategies != null ? `${data.engine.activeStrategies} 个活跃策略` : ((data as any).webAgentCount != null ? `${(data as any).webAgentCount} 个活跃策略` : '--')}
              </div>
            </div>

            {/* 风控状态 */}
            <div className={`rounded-lg p-2.5 ${data.riskControl?.isFrozen ? 'bg-red-500/10' : 'bg-muted/30'}`}>
              <div className="flex items-center gap-1 mb-1">
                <Shield className={`h-3 w-3 ${data.riskControl?.isFrozen ? 'text-red-400' : 'text-emerald-400'}`} />
                <span className="text-[10px] text-muted-foreground">风控</span>
              </div>
              {data.riskControl ? (
                <>
                  <div className={`text-sm font-bold ${data.riskControl.isFrozen ? 'text-red-400' : 'text-emerald-400'}`}>
                    {data.riskControl.isFrozen ? '已冻结' : '正常'}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    亏损 ¥{Math.abs(data.riskControl.dailyLoss || 0).toFixed(0)} · {data.riskControl.dailyTradeCount || 0}笔
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold text-muted-foreground">
                    {isConnected ? '无数据' : '--'}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {isConnected ? '引擎未返回风控信息' : '未连接'}
                  </div>
                </>
              )}
            </div>

            {/* 心跳状态 */}
            <div className={`rounded-lg p-2.5 ${heartbeatData?.pausedByHeartbeat ? 'bg-orange-500/10' : heartbeatData?.connected ? 'bg-emerald-500/10' : 'bg-muted/30'}`}>
              <div className="flex items-center gap-1 mb-1">
                <HeartPulse className={`h-3 w-3 ${heartbeatData?.pausedByHeartbeat ? 'text-orange-400' : heartbeatData?.connected ? 'text-emerald-400 animate-pulse' : 'text-muted-foreground'}`} />
                <span className="text-[10px] text-muted-foreground">心跳</span>
              </div>
              {heartbeatData ? (
                <>
                  <div className={`text-sm font-bold ${heartbeatData.pausedByHeartbeat ? 'text-orange-400' : heartbeatData.connected ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                    {heartbeatData.pausedByHeartbeat ? '已暂停' : heartbeatData.connected ? '正常' : '离线'}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {heartbeatData.pausedByHeartbeat
                      ? `失败${heartbeatData.consecutiveFails}次`
                      : heartbeatData.connected && heartbeatData.lastHeartbeat
                        ? `${Math.round((heartbeatData.elapsedMs || 0) / 1000)}s前`
                        : heartbeatData.message || '未连接'}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold text-muted-foreground">--</div>
                  <div className="text-[10px] text-muted-foreground">未配置</div>
                </>
              )}
            </div>

            {/* 引擎线程健康 - 20260421 根治文案 */}
            {(() => {
              const th = heartbeatData?.threadHealth;
              const threadEntries = th ? Object.entries(th) : [];
              const totalThreads = threadEntries.length;
              const aliveThreads = threadEntries.filter(([, info]: [string, any]) => info.alive).length;
              const deadThreads = totalThreads - aliveThreads;
              const hasDead = deadThreads > 0;
              const connected = heartbeatData?.connected;
              // [Fix] effectiveRunning: threadHealth 为空时用 runningCount 兜底
              const effectiveRunning = (heartbeatData as any)?.effectiveRunning ?? 0;
              const displayRunning = totalThreads > 0 ? aliveThreads : effectiveRunning;

              // 判断交易时段 (北京时间): 9:30-11:30 + 13:00-15:00 (工作日)
              const now = new Date();
              const beijing = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Shanghai" }));
              const hhmm = beijing.getHours() * 100 + beijing.getMinutes();
              const dow = beijing.getDay(); // 0=Sun 6=Sat
              const isWeekday = dow >= 1 && dow <= 5;
              const isTradingTime = isWeekday && ((hhmm >= 930 && hhmm <= 1130) || (hhmm >= 1300 && hhmm <= 1500));
              const isPreMarket = isWeekday && hhmm >= 900 && hhmm < 930;

              // 状态决策
              type Status = 'ok' | 'dead' | 'offline' | 'pending' | 'dormant' | 'premarket';
              let status: Status;
              // [Fix] 兜底：threadHealth 可能为空（心跳未携带），改用 t0EngineStatus.runningCount
              const t0Running = (heartbeatData?.t0EngineStatus?.runningCount ?? 0) as number;
              // dbRunningCount 来自 DB isRunning=1，最可靠的实时数据
              const anyRunning = totalThreads > 0 || t0Running > 0 || dbRunningCount > 0;
              if (totalThreads > 0 && !hasDead) status = 'ok';
              else if (anyRunning) status = 'ok';          // 任意数据源显示有策略在跑
              else if (totalThreads > 0 && hasDead) status = 'dead';
              else if (!connected) status = 'offline';
              else if (isTradingTime) status = 'pending';
              else if (isPreMarket) status = 'premarket';
              else status = 'dormant';

              const palette: Record<Status, { bg: string; fg: string; label: string; sub: string }> = {
                ok:        { bg: 'bg-emerald-500/10', fg: 'text-emerald-400',       label: `${aliveThreads || t0Running || dbRunningCount}存活`,    sub: `${aliveThreads || t0Running || dbRunningCount}/${totalThreads || t0Running || dbRunningCount} 线程正常` },
                dead:      { bg: 'bg-red-500/10',     fg: 'text-red-400',           label: `${deadThreads}死亡`,     sub: `${aliveThreads}/${totalThreads} 存活·${deadThreads}死亡` },
                offline:   { bg: 'bg-red-500/10',     fg: 'text-red-400',           label: '引擎离线',               sub: '心跳断开' },
                pending:   { bg: 'bg-amber-500/10',   fg: 'text-amber-400',         label: '待拉起',                 sub: '盘中无运行线程' },
                premarket: { bg: 'bg-muted/30',       fg: 'text-muted-foreground',  label: '盘前',                   sub: '等待 9:30 开盘' },
                dormant:   { bg: 'bg-muted/30',       fg: 'text-muted-foreground',  label: '休眠中',                 sub: '非交易时段' },
              };
              const s = palette[status];
              return (
                <div className={`rounded-lg p-2.5 ${s.bg}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <Cpu className={`h-3 w-3 ${s.fg}`} />
                    <span className="text-[10px] text-muted-foreground">引擎线程</span>
                  </div>
                  <div className={`text-sm font-bold ${s.fg}`}>{s.label}</div>
                  <div className="text-[10px] text-muted-foreground">{s.sub}</div>
                </div>
              );
            })()}

            {/* 版本/更新 */}
            {(() => {
              const engineVer = data.engine?.version || data.autoUpdate?.currentVersion || null;
              const webVer = webVersionData?.latestPublishedVersion || webVersionData?.fileVersion || null;
              // 语义化版本比较：只有当最新发布版本 > 引擎版本时才显示不一致
              const cmpVer = (a: string, b: string) => {
                const pa = (a || '0.0.0').replace(/-.*$/, '').split('.').map(Number);
                const pb = (b || '0.0.0').replace(/-.*$/, '').split('.').map(Number);
                for (let i = 0; i < 3; i++) { if ((pa[i]||0) !== (pb[i]||0)) return (pa[i]||0) - (pb[i]||0); }
                return 0;
              };
              // 只有当webVer > engineVer时才算“有新版本”
              const needsUpdate = engineVer && webVer && cmpVer(webVer, engineVer) > 0;
              const hasPending = data.autoUpdate?.pendingUpdate;
              const cardBg = hasPending || needsUpdate ? 'bg-amber-500/10' : 'bg-muted/30';
              return (
                <div className={`rounded-lg p-2.5 ${cardBg}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <Download className={`h-3 w-3 ${hasPending || needsUpdate ? 'text-amber-400' : 'text-muted-foreground'}`} />
                    <span className="text-[10px] text-muted-foreground">版本</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-sm font-bold font-mono ${hasPending || needsUpdate ? 'text-amber-400' : 'text-foreground'}`}>
                      {engineVer || '--'}
                    </span>
                    {needsUpdate && (
                      <span className="text-[10px] text-amber-400">→ {webVer}</span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {hasPending
                      ? `待更新 → ${data.autoUpdate?.pendingVersion}`
                      : needsUpdate
                        ? `新版本可用 ${webVer}`
                        : engineVer
                          ? '已是最新'
                          : webVer
                            ? `Web: ${webVer}`
                            : '无版本信息'}
                  </div>
                  {needsUpdate && webVer && (
                    <TriggerUpdateButton targetVersion={webVer} />
                  )}
                </div>
              );
            })()}
          </div>

          {/* [v2.0] HA 主备角色 + 节点健康 */}
          {systemState?.ha && (
            <div className="mt-3 pt-3 border-t border-border/30">
              {/* 当前角色提示条 */}
              {systemState.ha.currentLeader && (
                <div className="flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-blue-400 shrink-0" />
                    <div className="text-xs">
                      <span className="text-muted-foreground">当前使用</span>
                      <span className="ml-1.5 font-bold text-blue-400">{(systemState.ha.currentLeader as any).name}</span>
                      <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 h-4 border-blue-500/40 text-blue-400">
                        {(systemState.ha.currentLeader as any).role === 'primary' ? '主机' : '备机'}
                      </Badge>
                    </div>
                  </div>
                  {systemState.ha.multiPrimaryDetected && (
                    <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-400">
                      ⚠ 多主告警
                    </Badge>
                  )}
                </div>
              )}

              {/* 主备节点详情卡片 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {((systemState.ha.nodes as any[]) || []).map((node: any) => {
                  const statusColors: Record<string, { bg: string; fg: string; dot: string; label: string }> = {
                    healthy: { bg: 'bg-emerald-500/10 border-emerald-500/20', fg: 'text-emerald-400', dot: 'bg-emerald-400', label: '正常' },
                    degraded: { bg: 'bg-amber-500/10 border-amber-500/20', fg: 'text-amber-400', dot: 'bg-amber-400', label: '降级' },
                    offline: { bg: 'bg-red-500/10 border-red-500/20', fg: 'text-red-400', dot: 'bg-red-400', label: '离线' },
                    unknown: { bg: 'bg-muted/30 border-border/30', fg: 'text-muted-foreground', dot: 'bg-muted-foreground', label: '未知' },
                  };
                  const sc = statusColors[node.overallStatus] || statusColors.unknown;
                  const roleBadgeColor = node.role === 'primary'
                    ? 'border-blue-500/40 text-blue-400'
                    : 'border-purple-500/40 text-purple-400';
                  return (
                    <div key={node.engineId} className={`rounded-lg p-2.5 border ${sc.bg}`}>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`inline-block h-2 w-2 rounded-full ${sc.dot} ${node.overallStatus === 'healthy' ? 'animate-pulse' : ''}`} />
                          <span className="text-xs font-bold truncate">{node.name}</span>
                          <Badge variant="outline" className={`text-[9px] px-1 py-0 h-4 ${roleBadgeColor}`}>
                            {node.role === 'primary' ? '主机' : '备机'}
                          </Badge>
                          {node.isCurrentLeader && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-blue-500/40 text-blue-400">
                              使用中
                            </Badge>
                          )}
                        </div>
                        <span className={`text-[10px] font-bold ${sc.fg}`}>{sc.label}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px]">
                        <div className="flex items-center gap-1">
                          <span className={node.checks.processAlive ? 'text-emerald-400' : 'text-red-400'}>
                            {node.checks.processAlive ? '✓' : '✗'}
                          </span>
                          <span className="text-muted-foreground">进程</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={node.checks.heartbeatFresh ? 'text-emerald-400' : 'text-red-400'}>
                            {node.checks.heartbeatFresh ? '✓' : '✗'}
                          </span>
                          <span className="text-muted-foreground">心跳 {node.heartbeatAgeSec < 999 ? node.heartbeatAgeSec + 's' : '--'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={node.checks.brokerConnected ? 'text-emerald-400' : 'text-red-400'}>
                            {node.checks.brokerConnected ? '✓' : '✗'}
                          </span>
                          <span className="text-muted-foreground">券商</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={node.checks.miniQmtRunning ? 'text-emerald-400' : 'text-red-400'}>
                            {node.checks.miniQmtRunning ? '✓' : '✗'}
                          </span>
                          <span className="text-muted-foreground">miniQMT</span>
                        </div>
                      </div>

                      <div className="mt-1.5 pt-1.5 border-t border-border/30 text-[10px]">
                        {node.isCurrentLeader ? (
                          <span className="text-blue-400">● 当前主用引擎</span>
                        ) : node.canTakeover ? (
                          <span className="text-emerald-400">● 备用就绪 (可接管)</span>
                        ) : node.allOk && Number(node.isActive) === 0 ? (
                          <span className="text-muted-foreground">○ 健康但未启用</span>
                        ) : node.allOk && (!node.brokerConnected || !node.miniQmtRunning) ? (
                          <span className="text-amber-400">⚠ 进程在线但无法接管{node.overallReason ? '：' + node.overallReason : '（券商或miniQMT未连接）'}</span>
                        ) : (
                          <span className="text-amber-400">⚠ 不可接管{node.overallReason ? '：' + node.overallReason : ''}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* [Steven 2026-05-25] 重启历史入口 - 替代独立卡片 */}
              <RestartHistoryEntry />
            </div>
          )}


          {/* 延迟趋势图（可展开/收起） */}
          {showChart && (
            <div className="mt-3 pt-3 border-t border-border/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Activity className="h-3 w-3" />
                  延迟趋势
                </span>
                <div className="flex items-center gap-0.5">
                  {TIME_RANGES.map((range) => (
                    <Button
                      key={range.label}
                      variant={chartHours === range.hours ? "secondary" : "ghost"}
                      size="sm"
                      className={`h-5 px-2 text-[10px] ${chartHours === range.hours ? 'text-primary' : 'text-muted-foreground'}`}
                      onClick={() => setChartHours(range.hours)}
                    >
                      {range.label}
                    </Button>
                  ))}
                </div>
              </div>
              <LatencyChart hours={chartHours} />
            </div>
          )}

          {/* 风控冻结警告条 */}
          {data.riskControl?.isFrozen && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2">
              <ShieldAlert className="h-4 w-4 text-red-400 shrink-0" />
              <div className="text-xs text-red-300">
                <span className="font-medium">风控冻结</span>
                {data.riskControl.frozenReason && <span> — {data.riskControl.frozenReason}</span>}
              </div>
            </div>
          )}

          {/* 待更新提示条 */}
          {data.autoUpdate?.pendingUpdate && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
              <Download className="h-4 w-4 text-amber-400 shrink-0" />
              <div className="text-xs text-amber-300">
                <span className="font-medium">有新版本可用</span>
                <span> — {data.autoUpdate.pendingVersion}</span>
              </div>
            </div>
          )}
          </>)}
        </CardContent>
      </Card>

      <EngineDetailDialog open={showDetail} onOpenChange={setShowDetail} data={data} heartbeatData={heartbeatData} webVersionData={webVersionData} />
    </>
  );
}

// [Steven 2026-05-25] 重启历史入口 - 默认折叠, 点击展开 RestartHistoryWidget (embedded 模式)
function RestartHistoryEntry() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 pt-2 border-t border-border/30">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
      >
        <span className="flex items-center gap-1.5">
          <Activity className="h-3 w-3" />
          重启历史
        </span>
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <div className="mt-2">
          <RestartHistoryWidget embedded />
        </div>
      )}
    </div>
  );
}
